import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { type LegacyCharity, RequestError, api } from '../../lib/api'
import { faDate, faNum } from './labels'

/**
 * An organization's past in the portal, and bringing it in on demand.
 *
 * Shows what the portal holds before anything is written — how many
 * evaluations, how recent, and value by value what the profile would become —
 * because «refresh from old data» is only a safe button when you can see what
 * it is about to do. Nothing the charity said or confirmed since is ever
 * overwritten; the preview marks those rows so nobody has to take that on
 * trust.
 */
export function LegacyPanel({
  orgId,
  canManage,
  onClose,
}: {
  orgId: number
  canManage: boolean
  onClose: () => void
}) {
  const queryClient = useQueryClient()
  /* For an organization not yet bound to the portal: the record picked to preview and bind. */
  const [picked, setPicked] = useState<LegacyCharity | null>(null)
  const [assessment, setAssessment] = useState(true)
  const [profile, setProfile] = useState(true)
  const [done, setDone] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  const status = useQuery({
    queryKey: ['admin-legacy-status', orgId, picked?.id ?? null],
    queryFn: () => api.adminLegacyStatus(orgId, picked?.id),
  })

  const run = useMutation({
    mutationFn: () =>
      api.adminLegacyImport(orgId, {
        legacyCharityId: picked?.id ?? null,
        assessment,
        profile,
      }),
    onSuccess: (r) => {
      setError(null)
      const parts: string[] = []
      if (assessment) {
        parts.push(`${faNum(r.inserted)} مورد تازه از خودارزیابی آمد و ${faNum(r.refreshed)} مورد به‌روز شد`)
      }
      if (profile) {
        parts.push(
          `${faNum(r.factsWritten)} مورد در شناسنامه نوشته شد` +
            (r.factsKept > 0 ? ` و ${faNum(r.factsKept)} مورد که خود مؤسسه گفته بود دست نخورد` : ''),
        )
      }
      setDone(`${parts.join('. ')}.`)
      setPicked(null)
      void queryClient.invalidateQueries({ queryKey: ['admin-legacy-status', orgId] })
      void queryClient.invalidateQueries({ queryKey: ['admin-orgs'] })
    },
    onError: (err) => setError(err instanceof RequestError ? err.messageFa : 'انجام نشد.'),
  })

  const data = status.data
  const unbound = data !== undefined && data.legacyCharityId === null
  const hasRecord = data !== undefined && data.charityTitle !== null

  return (
    <div className="notice notice--info stack">
      <div className="row" style={{ justifyContent: 'space-between' }}>
        <strong>داده‌های قبلی در پرتال افرا</strong>
        <button type="button" className="btn btn--quiet btn--sm" onClick={onClose}>
          بستن
        </button>
      </div>

      {status.isLoading && <p className="muted">در حال بارگذاری…</p>}
      {status.isError && (
        <p className="notice notice--error">
          {status.error instanceof RequestError ? status.error.messageFa : 'خوانده نشد.'}
        </p>
      )}

      {unbound && !picked && (
        <>
          <p className="meta">
            این مؤسسه هنوز به پرونده‌ای در پرتال وصل نیست. اگر قبلاً در پرتال بوده، پرونده‌اش را پیدا کنید.
          </p>
          <CharitySearch onPick={setPicked} />
        </>
      )}

      {data && hasRecord && (
        <>
          <dl className="legacy-facts">
            <dt>پرونده</dt>
            <dd>
              {data.charityTitle}
              {unbound && (
                <>
                  {' '}
                  <button type="button" className="btn btn--quiet btn--sm" onClick={() => setPicked(null)}>
                    تغییر
                  </button>
                </>
              )}
            </dd>
            <dt>ارزیابی‌ها</dt>
            <dd>
              <span className="num">{faNum(data.evaluations)}</span>
              {data.lastEvaluatedOn && <> · آخرینش {faDate(data.lastEvaluatedOn)}</>}
            </dd>
            <dt>خودارزیابی</dt>
            <dd>
              {data.assessmentImportedOn
                ? <>آورده شده در {faDate(data.assessmentImportedOn)} · <span className="num">{faNum(data.ledgerFromLegacy)}</span> مورد هنوز همان جواب قبلی است</>
                : 'هنوز آورده نشده'}
              {' · '}
              <span className="num">{faNum(data.legacyStatements)}</span> مورد در پرتال
            </dd>
            <dt>شناسنامه</dt>
            <dd>{data.profileImportedOn ? `آورده شده در ${faDate(data.profileImportedOn)}` : 'هنوز آورده نشده'}</dd>
          </dl>

          {data.profile.length > 0 && (
            <div className="table-scroll">
              <table className="table">
                <thead>
                  <tr>
                    <th>مورد</th>
                    <th>در پرتال</th>
                    <th>الان در شناسنامه</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {data.profile.map((item) => (
                    <tr key={item.code}>
                      <td>{item.labelFa}</td>
                      <td>{item.valueFa}</td>
                      <td className="meta">{item.currentFa ?? 'خالی'}</td>
                      <td>
                        {item.willWrite ? (
                          <span className="badge badge--stored">نوشته می‌شود</span>
                        ) : (
                          <span className="badge badge--good">خود مؤسسه گفته، دست نمی‌خورد</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {canManage ? (
            <>
              <label className="check check--plain">
                <input type="checkbox" checked={assessment} onChange={(e) => setAssessment(e.target.checked)} />
                <span>{data.assessmentImportedOn ? 'به‌روز کردن خودارزیابی' : 'آوردن خودارزیابی'}</span>
                <span className="check__hint">
                  فقط مواردی عوض می‌شوند که هنوز همان جواب قبلی‌اند. هر چیزی که در گفت‌وگو یا با سند روشن شده
                  سر جایش می‌ماند.
                </span>
              </label>
              <label className="check check--plain">
                <input type="checkbox" checked={profile} onChange={(e) => setProfile(e.target.checked)} />
                <span>{data.profileImportedOn ? 'به‌روز کردن شناسنامه' : 'آوردن شناسنامه'}</span>
                <span className="check__hint">ردیف‌هایی که «دست نمی‌خورد» دارند عوض نمی‌شوند.</span>
              </label>
              {unbound && (
                <p className="meta">با این کار، این مؤسسه برای همیشه به همین پرونده در پرتال وصل می‌شود.</p>
              )}
              <div className="row">
                <button
                  type="button"
                  className="btn btn--sm"
                  disabled={run.isPending || (!assessment && !profile)}
                  onClick={() => run.mutate()}
                >
                  {run.isPending ? 'در حال انجام…' : 'انجام بده'}
                </button>
              </div>
            </>
          ) : (
            <p className="meta">آوردن داده‌های قبلی فقط از عهدهٔ مدیر ارشد برمی‌آید.</p>
          )}
        </>
      )}

      {done && <p className="notice notice--info">{done}</p>}
      {error && <p className="notice notice--error">{error}</p>}
    </div>
  )
}

function CharitySearch({ onPick }: { onPick: (c: LegacyCharity) => void }) {
  const [term, setTerm] = useState('')
  const results = useQuery({
    queryKey: ['admin-legacy', term],
    queryFn: () => api.adminLegacyCharities(term.trim()),
    enabled: term.trim().length >= 2,
  })
  return (
    <div className="field">
      <label htmlFor="legacy-search">نام مؤسسه در پرتال</label>
      <input id="legacy-search" value={term} onChange={(e) => setTerm(e.target.value)} placeholder="بخشی از نام" />
      {results.data && results.data.charities.length > 0 && (
        <div className="options" style={{ marginTop: 'var(--space-3)' }}>
          {results.data.charities.slice(0, 8).map((c) => (
            <button
              key={c.id}
              type="button"
              className="option"
              disabled={c.claimed}
              onClick={() => onPick(c)}
            >
              <span>
                <span>{c.title}</span>
                <span className="meta" style={{ display: 'block' }}>
                  <span className="num">{faNum(c.evaluations)}</span> ارزیابی
                  {c.claimed && ' · به مؤسسهٔ دیگری وصل است'}
                </span>
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
